import type { InstrumentType } from '@/domain/types';
import type { AutomatableParam } from '../effects/EffectNode';
import { defaultInstrumentParams, midiToFrequency } from './descriptors';
import { envelopeLevelAt, type EnvelopeShape } from './envelope';
import { MOD_WHEEL_CENTS, noiseBuffer, rampParam, registerInstrument, type InstrumentNode } from './Instrument';

/**
 * A two-oscillator subtractive synthesiser.
 *
 * Architecture:
 *
 *   per voice:  osc A ─┐
 *                      ├─▶ voice VCA (ADSR) ─▶ voice filter ─▶ output gain
 *               osc B ─┘                            ▲
 *                                                   │
 *                    shared cutoff source ──────────┤  (audio-rate sum into
 *                 shared resonance source ──────────┤   frequency and Q)
 *                    shared env source ─▶ voice env ┘
 *
 * The filter is per voice, because a shared one is not a filter on a note — it
 * is a filter on whatever is playing. Retrigger a shared filter and its
 * envelope restarts under every note already sounding: hold a low note, play a
 * high one, and the low note audibly opens up again, brightening a note nobody
 * touched. That is the one artefact of a shared filter that cannot be explained
 * as a design choice, so each voice now carries its own filter and its own
 * envelope, and a new note reshapes only itself.
 *
 * Cutoff and resonance are still single `AudioParam`s. A `ConstantSourceNode`
 * for each is fanned out to every voice's `frequency` and `Q`, so automation
 * ramps one param and every voice follows — nothing has to walk the voice list,
 * and a voice created mid-ramp picks the ramp up where it is. The intrinsic
 * values are zeroed so the connected sources are the whole story; an
 * `AudioParam` adds every connected signal to its own value, and a non-zero
 * intrinsic would be an invisible offset on top of the automation.
 *
 * The cost is real — a ten-note chord is ten filters rather than one — and it
 * is the right way round: filters are cheap, and a note that changes shape
 * because of a different note is a bug you can hear.
 */

const MAX_VOICES = 24;
const WAVE_TYPES: OscillatorType[] = ['sine', 'triangle', 'sawtooth', 'square'];

interface Voice {
  oscA: OscillatorNode;
  oscB: OscillatorNode;
  gain: GainNode;
  filter: BiquadFilterNode;
  /** This voice's share of the filter envelope, in Hz at full depth. */
  envAmount: GainNode;
  pitch: number;
  startedAt: number;
  /** Context time the voice is scheduled to finish, or Infinity while held. */
  endsAt: number;
  released: boolean;
  /** The amplitude envelope as scheduled, so a release can start from where it is. */
  envelope: EnvelopeShape;
}

class Synth implements InstrumentNode {
  readonly type: InstrumentType = 'synth';
  readonly output: GainNode;

  private readonly bendSource: ConstantSourceNode;
  private readonly cutoffSource: ConstantSourceNode;
  private readonly resonanceSource: ConstantSourceNode;
  private readonly envSource: ConstantSourceNode;
  private readonly modSource: ConstantSourceNode;
  private readonly voices: Voice[] = [];
  private params: Record<string, number>;

  constructor(private readonly ctx: BaseAudioContext) {
    this.params = defaultInstrumentParams('synth');

    this.output = ctx.createGain();

    // Bend is shared rather than per voice, because that is what bending is:
    // one hand on one wheel moving every note that is sounding. Held in cents,
    // which is the unit `detune` already speaks, so it can be connected straight
    // into every oscillator without a conversion node in between.
    this.bendSource = ctx.createConstantSource();
    this.bendSource.offset.value = this.params.bend * 100;
    this.bendSource.start();

    this.cutoffSource = ctx.createConstantSource();
    this.cutoffSource.offset.value = this.params.cutoff;
    this.cutoffSource.start();

    this.resonanceSource = ctx.createConstantSource();
    this.resonanceSource.offset.value = this.params.resonance;
    this.resonanceSource.start();

    this.modSource = ctx.createConstantSource();
    this.modSource.offset.value = (this.params.modWheel ?? 0) * MOD_WHEEL_CENTS;
    this.modSource.start();

    // One envelope source at a constant 1, scaled per voice. The shape is the
    // voice's; only the constant is shared.
    this.envSource = ctx.createConstantSource();
    this.envSource.offset.value = 1;
    this.envSource.start();
  }

  setParam(paramId: string, value: number, atTime?: number): void {
    this.params[paramId] = value;
    switch (paramId) {
      case 'bend':
        rampParam(this.bendSource.offset, value * 100, this.ctx, atTime);
        break;
      case 'cutoff':
        rampParam(this.cutoffSource.offset, value, this.ctx, atTime);
        break;
      case 'resonance':
        rampParam(this.resonanceSource.offset, value, this.ctx, atTime);
        break;
      case 'modWheel':
        rampParam(this.modSource.offset, value * MOD_WHEEL_CENTS, this.ctx, atTime);
        break;
      case 'volume':
        rampParam(this.output.gain, value, this.ctx, atTime);
        break;
      default:
        // Oscillator shape, envelope times and detune are read when a voice is
        // created; changing them affects the next note, as on a real synth.
        break;
    }
  }

  paramTargets(paramId: string): AutomatableParam[] {
    const identity = (v: number): number => v;
    switch (paramId) {
      // Semitones on the outside, cents on the inside: a musician bends by a
      // tone, and `detune` counts in hundredths of one.
      case 'bend':
        return [{ param: this.bendSource.offset, map: (v) => v * 100 }];
      case 'cutoff':
        return [{ param: this.cutoffSource.offset, map: identity }];
      case 'resonance':
        return [{ param: this.resonanceSource.offset, map: identity }];
      case 'modWheel':
        return [{ param: this.modSource.offset, map: (v) => v * MOD_WHEEL_CENTS }];
      case 'volume':
        return [{ param: this.output.gain, map: identity }];
      default:
        return [];
    }
  }

  setState(): void {
    // This instrument keeps no structured state beyond its parameters.
  }

  playNote(pitch: number, velocity: number, when: number, duration: number): void {
    const voice = this.startVoice(pitch, velocity, when);
    if (voice) this.releaseVoice(voice, when + Math.max(0.005, duration));
  }

  noteOn(pitch: number, velocity: number, when: number): void {
    this.startVoice(pitch, velocity, when);
  }

  noteOff(pitch: number, when: number): void {
    // Release the most recent unreleased voice at this pitch: repeated notes
    // should not cut off the one still ringing from an earlier press.
    for (let i = this.voices.length - 1; i >= 0; i--) {
      const voice = this.voices[i];
      if (voice.pitch === pitch && !voice.released) {
        this.releaseVoice(voice, when);
        return;
      }
    }
  }

  allNotesOff(when: number): void {
    for (const voice of [...this.voices]) this.releaseVoice(voice, when, 0.01);
  }

  private startVoice(pitch: number, velocity: number, when: number): Voice | null {
    const ctx = this.ctx;
    const p = this.params;

    this.reap(when);
    if (this.voices.length >= MAX_VOICES) {
      // Steal the oldest sounding voice rather than refusing the note: a
      // dropped note is far more noticeable than a shortened one.
      const oldest = this.voices.reduce((a, b) => (a.startedAt <= b.startedAt ? a : b));
      this.releaseVoice(oldest, when, 0.01);
    }

    const frequency = midiToFrequency(pitch + p.octave * 12);
    if (!Number.isFinite(frequency) || frequency <= 0) return null;

    const wave = WAVE_TYPES[Math.min(WAVE_TYPES.length - 1, Math.max(0, Math.round(p.wave)))];

    // This voice's filter, and its share of the shared cutoff and resonance.
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 0;
    filter.Q.value = 0;
    filter.connect(this.output);
    this.cutoffSource.connect(filter.frequency);
    this.resonanceSource.connect(filter.Q);
    this.modSource.connect(filter.detune);

    const envAmount = ctx.createGain();
    envAmount.gain.value = 0;
    this.envSource.connect(envAmount).connect(filter.frequency);

    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(filter);

    const oscA = ctx.createOscillator();
    oscA.type = wave;
    oscA.frequency.value = frequency;
    // The shared bend adds to whatever spread this voice was built with, so a
    // bend moves the pair together and the detune between them survives it.
    oscA.detune.value = -p.detune;
    this.bendSource.connect(oscA.detune);
    oscA.connect(gain);

    const oscB = ctx.createOscillator();
    oscB.type = wave;
    oscB.frequency.value = frequency;
    oscB.detune.value = p.detune;
    this.bendSource.connect(oscB.detune);
    oscB.connect(gain);

    // Amplitude envelope. Two oscillators sum, and a saw already peaks at full
    // scale, so a single voice is scaled well below unity — a chord has to fit
    // in the same headroom, and clipping the master on one note is not a sound
    // anyone asked for.
    const peak = (Math.max(1, Math.min(127, velocity)) / 127) * 0.3;
    const sustain = peak * p.sustain;
    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(peak, when + p.attack);
    gain.gain.linearRampToValueAtTime(sustain, when + p.attack + p.decay);

    this.triggerFilterEnvelope(envAmount.gain, when);

    oscA.start(when);
    oscB.start(when);

    const voice: Voice = {
      oscA,
      oscB,
      gain,
      filter,
      envAmount,
      envelope: { startedAt: when, attack: p.attack, decay: p.decay, peak, sustain },
      pitch,
      startedAt: when,
      endsAt: Infinity,
      released: false,
    };
    this.voices.push(voice);
    return voice;
  }

  /**
   * Opens this voice's filter at note-on and closes it over the decay time.
   *
   * It is scheduled on the voice's own gain, which is the whole point: nothing
   * here can be cancelled or re-shaped by a later note, because a later note
   * has a different `AudioParam` to write to.
   */
  private triggerFilterEnvelope(gain: AudioParam, when: number): void {
    const p = this.params;
    if (p.filterEnv <= 0) return;
    const depth = p.filterEnv * Math.min(12000, p.cutoff * 6);
    try {
      gain.setValueAtTime(0, when);
      gain.linearRampToValueAtTime(depth, when + Math.max(0.001, p.attack));
      gain.linearRampToValueAtTime(0, when + Math.max(0.002, p.attack + p.decay));
    } catch {
      gain.value = 0;
    }
  }

  private releaseVoice(voice: Voice, when: number, releaseOverride?: number): void {
    if (voice.released && when >= voice.endsAt) return;
    const release = releaseOverride ?? this.params.release;
    const end = when + Math.max(0.005, release);

    voice.released = true;
    voice.endsAt = end;

    try {
      voice.gain.gain.cancelScheduledValues(when);
      // Anchoring at the level the envelope will have reached keeps the release
      // starting from there rather than jumping first. Worked out, not read:
      // the gain's `value` is where it is now, and now is ahead of `when` by
      // the lookahead live and by the whole piece in a render.
      const from = envelopeLevelAt(voice.envelope, when);
      voice.envelope.release = { at: when, from, end };
      voice.gain.gain.setValueAtTime(from, when);
      voice.gain.gain.linearRampToValueAtTime(0, end);
      voice.oscA.stop(end);
      voice.oscB.stop(end);
    } catch {
      /* the voice may already have been stopped */
    }

    voice.oscA.onended = () => this.disposeVoice(voice);
  }

  private disposeVoice(voice: Voice): void {
    const index = this.voices.indexOf(voice);
    if (index >= 0) this.voices.splice(index, 1);
    voice.oscA.onended = null;
    try {
      voice.oscA.disconnect();
      voice.oscB.disconnect();
      voice.gain.disconnect();
      voice.envAmount.disconnect();
      voice.filter.disconnect();
      // The shared sources have to let go of this voice's parameters too.
      // Disconnecting a node only drops what it feeds, not what feeds it, so
      // without this the four sources would hold a reference to every parameter
      // of every voice ever played — bounded by the length of the session
      // rather than by polyphony.
      this.bendSource.disconnect(voice.oscA.detune);
      this.bendSource.disconnect(voice.oscB.detune);
      this.cutoffSource.disconnect(voice.filter.frequency);
      this.resonanceSource.disconnect(voice.filter.Q);
      this.modSource.disconnect(voice.filter.detune);
      this.envSource.disconnect(voice.envAmount);
    } catch {
      /* already disconnected */
    }
  }

  /**
   * Forgets voices that will have finished by `now`, so they stop counting
   * against polyphony.
   *
   * It deliberately does **not** disconnect them. Offline rendering schedules
   * the whole piece before a single sample is produced, so at the moment a bar
   * 4 note is scheduled, the bar 1 note has not actually played yet — tearing
   * its nodes down here would delete it from the render. Disconnection is left
   * to `onended`, which fires when the audio really has finished, and to
   * `dispose()` when the instrument goes away.
   */
  private reap(now: number): void {
    for (let i = this.voices.length - 1; i >= 0; i--) {
      if (this.voices[i].endsAt < now) this.voices.splice(i, 1);
    }
  }

  dispose(): void {
    for (const voice of [...this.voices]) this.disposeVoice(voice);
    try {
      this.cutoffSource.stop();
      this.resonanceSource.stop();
      this.modSource.stop();
      this.envSource.stop();
    } catch {
      /* not started, or already stopped */
    }
    this.cutoffSource.disconnect();
    this.resonanceSource.disconnect();
    this.modSource.disconnect();
    this.envSource.disconnect();
    this.output.disconnect();
  }
}

registerInstrument('synth', (ctx) => new Synth(ctx));

/** Re-exported so the drum machine can share the noise source. */
export { noiseBuffer };
